import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, ReplaySubject, defer, throwError } from 'rxjs';
import { catchError, map, share, tap } from 'rxjs/operators';
import { AuthService } from './api/auth-api';

@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {
  private readonly auth = inject(AuthService);

  /**
   * Concurrent subscribers share a single refresh request. Once it settles the
   * next subscriber starts a new one. The refresh keeps running when every
   * subscriber leaves, so the new tokens are never lost. A failed refresh ends
   * the session.
   */
  private readonly refresh$ = defer(() => {
    const refreshToken = this.auth.getRefreshToken();
    return refreshToken
      ? this.auth.refreshToken$(refreshToken)
      : throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              statusText: 'No refresh token available'
            })
        );
  }).pipe(
    tap(authCookie => this.auth.handleAuthCookie(authCookie)),
    map(authCookie => authCookie.idToken),
    catchError((error: unknown) => {
      this.auth.logout();
      return throwError(() => error);
    }),
    share({
      connector: () => new ReplaySubject<string>(1),
      resetOnRefCountZero: false
    })
  );

  refreshIdToken$(): Observable<string> {
    return this.refresh$;
  }
}
